import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlanningStore } from '@/stores/planning';
import { useAuthStore } from '@/stores/auth';
import { formatDateKey, getWeekDates, groupTasksForGrid, groupTasksFlatInstallers, getFeestdagDates, countVisibleTasks, isFeestdagMarker, isVerlofTask } from '@/utils/planning';
import { applyInstallerRowOrder } from '@/utils/planningRowOrder';
import { planningEmployeesOnly, filterEmployeeUserMap, filterEmployeeTasks } from '@/utils/planningScope';
import { groupTasksPerDay, countPerDayTasks } from '@/utils/planningPerDay';
import { getBoardDays } from '@/utils/planningWeeks';
import { PlanningHeader } from '@/components/planning/PlanningHeader';
import { PlanningGrid } from '@/components/planning/PlanningGrid';
import { PlanningDayBoard } from '@/components/planning/PlanningDayBoard';
import { PlanningProjectSearch } from '@/components/planning/PlanningProjectSearch';
import { PlanningModeBanner } from '@/components/planning/PlanningModeBanner';
import { PlanningWarningToast } from '@/components/planning/PlanningWarningToast';
import { TaskDetailPopup } from '@/components/project/TaskDetailPopup';
import type { OdooTask, ProjectTask } from '@/types/odoo';

/**
 * Adapt a planning-board `OdooTask` into the `ProjectTask` shape the detail popup
 * consumes. `OdooTask` is a superset, but its Studio fields are optional there and
 * required on `ProjectTask`, so fill the same "empty" defaults Odoo returns (`false`
 * / `[]`) rather than a lossy cast. This is the same editor the dashboard opens; the
 * whole-fase file attachments resolve from `parent_id` (the fase task).
 */
function odooTaskToProjectTask(t: OdooTask): ProjectTask {
  return {
    id: t.id,
    name: t.name,
    stage_id: t.stage_id,
    date_deadline: t.date_deadline,
    x_studio_verdieping: t.x_studio_verdieping ?? false,
    x_studio_installateur: t.x_studio_installateur ?? [],
    x_studio_weergeven_in_planning: t.x_studio_weergeven_in_planning,
    parent_id: t.parent_id ?? false,
    x_studio_opgelijst: t.x_studio_opgelijst,
    description: t.description,
    x_studio_planningstage: t.x_studio_planningstage,
  };
}

/** Whole-week offset (relative to this week's Monday) for a yyyy-mm-dd date. */
export function weekOffsetForDate(dateStr: string): number {
  const mondayOf = (d: Date): Date => {
    const x = new Date(d);
    const dow = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - dow);
    x.setHours(12, 0, 0, 0);
    return x;
  };
  const target = mondayOf(new Date(`${dateStr}T12:00:00`));
  const today = mondayOf(new Date());
  return Math.round((target.getTime() - today.getTime()) / (7 * 24 * 3600 * 1000));
}

interface PlanningMountStore {
  setProjectFilter: (q: string) => void;
  setProjectIdFilter: (id: number | null) => void;
  setWeekOffset: (offset: number) => void;
  fetchPlanning: (offset?: number) => unknown;
}

/**
 * Translate the "Bekijk in Planning" deeplink (?q=&week=) into store mutations +
 * a single fetch. Extracted + exported so the branching is unit-testable.
 * - week deeplink → setWeekOffset + fetchPlanning(offset) (land on THAT week).
 * - no week → fetchPlanning() with NO arg, so the fetched range uses the store's
 *   current (possibly resumed) weekOffset and the grid never diverges from it.
 * Returns whether the URL params should be stripped afterwards.
 */
export function resolvePlanningMount(
  params: { q: string | null; week: string | null; pid: string | null },
  store: PlanningMountStore,
): { stripParams: boolean } {
  const { q, week, pid } = params;
  if (q !== null) store.setProjectFilter(q);
  const pidNum = pid !== null ? parseInt(pid, 10) : NaN;
  store.setProjectIdFilter(!isNaN(pidNum) && pidNum > 0 ? pidNum : null);
  if (week && /^\d{4}-\d{2}-\d{2}$/.test(week)) {
    const offset = weekOffsetForDate(week);
    store.setWeekOffset(offset);
    store.fetchPlanning(offset);
  } else {
    store.fetchPlanning();
  }
  return { stripParams: q !== null || week !== null || pid !== null };
}

export function PlanningView() {
  const {
    tasks,
    userMap,
    projectleiderMap,
    loading,
    error,
    weekOffset,
    viewFilter,
    collapsedGroups,
    toggleGroup,
    installerRowOrder,
  } = usePlanningStore();

  // Administratie on its own sees only our own employees, in the flat installer
  // list; with a role that sees the full planning it gets that role's planning
  // (utils/planningScope.ts).
  const employeesOnly = useAuthStore((s) => planningEmployeesOnly(s.hasRole));
  // Warehouse gets the planning board read-only as a cross-project "where is
  // everyone today" overview. Like administratie it always sees the flat
  // installer list (no PL grouping), but UNLIKE administratie it sees ALL
  // installers incl. subcontractors — no employee-only filtering below.
  const isWarehouse = useAuthStore((s) => s.hasRole('warehouse'));
  const forceFlatInstallers = employeesOnly || isWarehouse;
  const employeeMap = usePlanningStore((s) => s.employeeMap);

  const [searchParams, setSearchParams] = useSearchParams();

  // Clicking a card's "Details en beschrijving" button opens the SAME fase text +
  // fase-bestanden editor the dashboard scheduler uses. The card already carries
  // everything it needs (project id, fase = parent_id, task description).
  const [popupTask, setPopupTask] = useState<OdooTask | null>(null);

  // TaskDetailPanel wants installer id → name as a Record; the board holds it as a Map.
  const installerNames = useMemo(() => {
    const rec: Record<string, string> = {};
    for (const [id, name] of userMap) rec[String(id)] = name;
    return rec;
  }, [userMap]);

  // Mount fetch + deeplink seeding. "Bekijk in Planning" (dashboard) links here
  // with ?q=<projectnaam>&week=<yyyy-mm-dd>: seed the "Zoek project" grid filter
  // and land on that week. Runs once on mount; we then strip the params so a
  // later manual week/filter change isn't re-applied.
  // Preferences first: "Scroll door weken" decides the fetched date range, so
  // fetching before they arrive would load one week and then reload four.
  useEffect(() => {
    const params = { q: searchParams.get('q'), week: searchParams.get('week'), pid: searchParams.get('pid') };
    let cancelled = false;
    // Spinner while preferences load, instead of a flash of an empty board.
    usePlanningStore.setState({ loading: true });
    void usePlanningStore.getState().hydrateInstallerRowOrder().then(() => {
      if (cancelled) return;
      const s = usePlanningStore.getState();
      const { stripParams } = resolvePlanningMount(
        params,
        { setProjectFilter: s.setProjectFilter, setProjectIdFilter: s.setProjectIdFilter, setWeekOffset: s.setWeekOffset, fetchPlanning: s.fetchPlanning },
      );
      if (stripParams) setSearchParams({}, { replace: true });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: read the deeplink once and fetch
  }, []);

  const clearError = usePlanningStore((s) => s.clearError);
  const showAllInstallers = usePlanningStore((s) => s.showAllInstallers);
  const hideCompleted = usePlanningStore((s) => s.hideCompleted);
  const eindeWerkenProjects = usePlanningStore((s) => s.eindeWerkenProjects);
  const gevorderdFases = usePlanningStore((s) => s.gevorderdFases);
  const projectFilter = usePlanningStore((s) => s.projectFilter);
  const projectIdFilter = usePlanningStore((s) => s.projectIdFilter);
  const projectNameMap = usePlanningStore((s) => s.projectNameMap);
  const easykitConfig = usePlanningStore((s) => s.easykitConfig);
  const multiWeek = usePlanningStore((s) => s.multiWeek);

  // Auto-dismiss error toast after 4 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => clearError(), 4000);
    return () => clearTimeout(timer);
  }, [error, clearError]);

  // Administratie + warehouse always see the flat installer view
  const effectiveViewFilter = forceFlatInstallers ? 'installers' : viewFilter;

  // Administratie on its own: a userMap filtered to employees only
  const effectiveUserMap = useMemo(
    () => (employeesOnly ? filterEmployeeUserMap(userMap, employeeMap) : userMap),
    [employeesOnly, userMap, employeeMap],
  );

  // Filter tasks by project name when search filter is active
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    // Administratie on its own: only tasks where at least one installer is an employee
    if (employeesOnly) filtered = filterEmployeeTasks(filtered, employeeMap);

    if (hideCompleted) {
      filtered = filtered.filter((task) => {
        if (isFeestdagMarker(task) || isVerlofTask(task)) return true;
        const pid = task.project_id ? task.project_id[0] : 0;
        if (eindeWerkenProjects.has(pid)) return false;
        const parentId = task.parent_id ? task.parent_id[0] : 0;
        if (gevorderdFases.has(parentId)) return false;
        return true;
      });
    }

    const trimmed = projectFilter.trim();
    const hasTextFilter = trimmed.length >= 2;
    if (!hasTextFilter && !projectIdFilter) return filtered;
    const lowerFilter = trimmed.toLowerCase();
    return filtered.filter((task) => {
      // Keep feestdag markers — they're day-column structural, not project-specific.
      // Verlof is intentionally NOT kept here: when narrowing to a werf the user
      // doesn't want installer vacantie cluttering the result.
      if (isFeestdagMarker(task)) return true;
      // Deeplink project-ID match (reliable fallback when sale.order.project_name
      // differs from project.project.name, e.g. "(25313500)" suffix only on SO).
      if (projectIdFilter !== null) {
        const pid = task.project_id ? task.project_id[0] : 0;
        if (pid === projectIdFilter) return true;
      }
      if (!hasTextFilter) return false;
      // Match project name from projectNameMap
      const soId = Array.isArray(task.sale_order_id) ? task.sale_order_id[0] : null;
      const projectName = soId ? projectNameMap.get(soId) : null;
      if (projectName && projectName.toLowerCase().includes(lowerFilter)) return true;
      // Match project_id display name
      if (task.project_id && task.project_id[1] && String(task.project_id[1]).toLowerCase().includes(lowerFilter)) return true;
      // Match task name
      if (task.name && task.name.toLowerCase().includes(lowerFilter)) return true;
      // Match linked sale-order name (e.g. `S26316301`) — typing the
      // S-number or any substring of it narrows to that project's tasks.
      const soName = Array.isArray(task.sale_order_id) ? task.sale_order_id[1] : null;
      if (soName && String(soName).toLowerCase().includes(lowerFilter)) return true;
      return false;
    });
  }, [tasks, projectFilter, projectIdFilter, projectNameMap, employeesOnly, employeeMap, hideCompleted, eindeWerkenProjects, gevorderdFases]);

  // Board days: the selected Mon–Sat, or the multi-week window around it.
  const weekData = useMemo(() => ({ days: getBoardDays(weekOffset, multiWeek) }), [weekOffset, multiWeek]);
  const focusWeekKey = useMemo(
    () => (multiWeek ? formatDateKey(getWeekDates(weekOffset).days[0]) : undefined),
    [weekOffset, multiWeek],
  );

  // For administratie + warehouse, always show all installer rows (even empty ones)
  const effectiveShowAll = forceFlatInstallers ? true : showAllInstallers;

  // PL + Inst: grouped by projectleider → installer
  const groups = useMemo(
    () => groupTasksForGrid(filteredTasks, weekData.days, effectiveUserMap, projectleiderMap, effectiveShowAll, easykitConfig),
    [filteredTasks, weekData.days, effectiveUserMap, projectleiderMap, effectiveShowAll, easykitConfig],
  );

  // Flat installer rows (no PL headers)
  const flatRows = useMemo(
    () => groupTasksFlatInstallers(filteredTasks, weekData.days, effectiveUserMap, effectiveShowAll, employeeMap, easykitConfig),
    [filteredTasks, weekData.days, effectiveUserMap, effectiveShowAll, employeeMap, easykitConfig],
  );

  // Display only: rank the visible rows by the full saved order, leaving rows not
  // in the saved order after them in their incoming order. The saved order is the
  // single source of truth — it is only mutated by an explicit drag (PlanningGrid),
  // never auto-reconciled here, so the order is stable across weeks and the
  // "Toon alle installateurs" toggle.
  const orderedFlatRows = useMemo(
    () => applyInstallerRowOrder(flatRows, installerRowOrder),
    [flatRows, installerRowOrder],
  );

  // "Per dag": one column per day, each task once (only grouped when shown).
  const dayColumns = useMemo(
    () => (effectiveViewFilter === 'per_day' ? groupTasksPerDay(filteredTasks, weekData.days, projectNameMap, easykitConfig) : null),
    [effectiveViewFilter, filteredTasks, weekData.days, projectNameMap, easykitConfig],
  );

  // Feestdag marker dates (for purple column rendering)
  const feestdagDates = useMemo(() => getFeestdagDates(filteredTasks), [filteredTasks]);

  // Count unique visible tasks in the current grid view
  const filteredCount = useMemo(
    () => (dayColumns ? countPerDayTasks(dayColumns) : countVisibleTasks(
      effectiveViewFilter === 'pl_inst' ? groups : undefined,
      effectiveViewFilter === 'installers' ? orderedFlatRows : undefined,
    )),
    [dayColumns, effectiveViewFilter, groups, orderedFlatRows],
  );

  return (
    <div>
      {/* Not mounted in "Per dag": its fase picker keeps a selected project in local
          state and can create a fase, which the overview must not offer. */}
      {effectiveViewFilter !== 'per_day' && <PlanningProjectSearch />}
      <PlanningHeader filteredCount={filteredCount} />
      <PlanningModeBanner />

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div
            className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
            role="status"
          >
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Planning laden...</p>
        </div>
      )}

      <PlanningWarningToast />

      {/* Error toast — floating, auto-dismisses */}
      {error && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-in" role="alert">
          <span className="text-sm font-semibold">{error}</span>
          <button type="button" onClick={clearError} className="p-0.5 hover:bg-white/20 rounded" aria-label="Sluiten">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Grid — same week overview on every breakpoint. On mobile the cards drop
          their inline icon controls and a tap opens the action sheet instead. */}
      {!loading && dayColumns && (
        <PlanningDayBoard
          columns={dayColumns}
          days={weekData.days}
          feestdagDates={feestdagDates}
          onCardClick={setPopupTask}
          focusWeekKey={focusWeekKey}
        />
      )}
      {!loading && !dayColumns && (
        <PlanningGrid
          groups={effectiveViewFilter === 'pl_inst' ? groups : undefined}
          flatRows={effectiveViewFilter === 'installers' ? orderedFlatRows : undefined}
          days={weekData.days}
          feestdagDates={feestdagDates}
          collapsedGroups={collapsedGroups}
          onToggleCollapse={toggleGroup}
          onCardClick={setPopupTask}
          focusWeekKey={focusWeekKey}
        />
      )}

      {popupTask && (
        <TaskDetailPopup
          projectId={Array.isArray(popupTask.project_id) ? popupTask.project_id[0] : 0}
          faseId={Array.isArray(popupTask.parent_id) ? popupTask.parent_id[0] : null}
          task={odooTaskToProjectTask(popupTask)}
          installerNames={installerNames}
          // "Per dag" is an overview: its details open read-only.
          readOnly={effectiveViewFilter === 'per_day'}
          onClose={() => setPopupTask(null)}
        />
      )}
    </div>
  );
}
